import { yupResolver } from "@hookform/resolvers/yup"
import PropTypes from "prop-types"
import { Fragment } from "react"
import { useForm, Controller } from "react-hook-form"
import styled from "styled-components"
import * as Yup from "yup"

import Button from "components/ui/button"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselScrollSnaps,
} from "components/ui/carousel"
import Divider from "components/ui/divider"
import Image from "components/ui/image"
import Input from "components/ui/input"
import Modal from "components/ui/modal"
import Radio from "components/ui/radio"
import Textarea from "components/ui/textarea"
import { jewlr_t } from "helpers/application"
import useWindowDimensions from "helpers/window-dimensions"
import { cn } from "styles/utils"

const PreviewText = ({ className = "", ...props }) => (
  <p className={cn("whitespace-pre-wrap", className)} {...props} />
)
const PreviewDesc = ({ ...props }) => (
  <p
    className="text-14 text-grey-55 leading-14 text-center font-light"
    {...props}
  />
)

const PreviewBox = styled.div`
  @font-face {
    font-display: swap;
    font-family: "Oooh Baby";
    font-style: normal;
    font-weight: normal;
    src: url("${require("fonts/oooh-baby/oooh-baby-regular.ttf")}")
      format("truetype");
  }
  background: ${(props) => props.theme.colors.white};
  border: 1px solid ${(props) => props.theme.colors.grey[10]};
  display: flex;
  flex-direction: column;
  font-family: "Oooh Baby", serif;
  font-size: 10px;
  height: 160px;
  justify-content: center;
  letter-spacing: -0.02em;
  line-height: 13px;
  margin-bottom: 10px;
  max-height: 160px;
  max-width: 160px;
  overflow: hidden;
  overflow-wrap: break-word;
  padding: 14px;
  width: 160px;
`

const MAX_CHARS = 200
const MAX_LINES = 7

const schema = Yup.object().shape({
  message: Yup.string(),
  recipient: Yup.string().max(30, "Maximum 30 characters"),
  sender: Yup.string().max(30, "Maximum 30 characters"),
})

const GreetingCardModal = ({
  greetingCardProps,
  handleGreetingCard,
  onClose,
  selectedCardOption,
  show,
}) => {
  if (!selectedCardOption) return null
  const { isMobile } = useWindowDimensions()
  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    setError,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      message: greetingCardProps?.message || "",
      recipient: greetingCardProps?.recipient || "",
      sender: greetingCardProps?.sender || "",
    },
    mode: "onBlur",
    resolver: yupResolver(schema),
  })

  const validateMsg = (message) => {
    const rows = message.split("\n")
    const newRows = rows.length > MAX_LINES ? rows.slice(0, MAX_LINES) : rows
    return newRows.join("\n")
  }

  const handleChange = (e, setValue) => {
    const text = e.target.value
    const keycode = e.keycode || e.which || e.charCode
    const rows = text.split("\n")
    if (
      (rows.length >= MAX_LINES && keycode === 13) ||
      (text.length >= MAX_CHARS && ![8, 46].includes(keycode))
    ) {
      e.preventDefault()
    }
    setValue("message", validateMsg(text))
  }

  const handleDrop = (e, setValue) => {
    e.preventDefault()
    const text = e.dataTransfer.getData("text")
    setValue("message", validateMsg(text))
  }

  const onSubmit = async (data) => {
    try {
      await new Promise((res, rej) => {
        handleGreetingCard(
          { ...selectedCardOption, ...data },
          {
            onError: (err) => rej(err),
            onSuccess: () => {
              onClose()
              res()
            },
          }
        )
      })
    } catch (err) {
      setError("root.error", { message: err })
    }
  }

  const charsLeft = MAX_CHARS - watch("message")?.length
  const overLimit = charsLeft < 0

  return (
    <Modal
      headerVariant="inline"
      label={`Greeting Card ${jewlr_t("terms.Personalization")}`}
      onClose={onClose}
      show={show}
      width="744px"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        {isMobile ? (
          <Carousel
            className="mb-40"
            description="Greeting card front and back view"
          >
            <CarouselContent>
              <CarouselItem>
                <div className="flex flex-col items-center">
                  <PreviewBox>
                    <PreviewText className="mb-16">
                      {watch("recipient")}
                    </PreviewText>
                    <PreviewText>{watch("message")}</PreviewText>
                    <PreviewText className="mt-16 text-center">
                      {watch("sender")}
                    </PreviewText>
                  </PreviewBox>
                  <PreviewDesc>Inside of Greeting Card</PreviewDesc>
                </div>
              </CarouselItem>
              <CarouselItem>
                <div className="flex flex-col items-center">
                  <Image
                    alt="Front of greeting card"
                    className="w-160 mb-10"
                    height="320"
                    src={selectedCardOption.src_image_url}
                    width="320"
                  />
                  <PreviewDesc>Front of Greeting Card</PreviewDesc>
                </div>
              </CarouselItem>
            </CarouselContent>
            <CarouselScrollSnaps />
          </Carousel>
        ) : (
          <div className="mb-24 flex justify-center gap-20">
            <div>
              <Image
                alt="Front of greeting card"
                className="w-160 mb-10"
                height="320"
                mb="10px"
                ratio="1 / 1"
                src={selectedCardOption.src_image_url}
                width="320"
              />
              <PreviewDesc>Front</PreviewDesc>
            </div>
            <div className="flex flex-col">
              <PreviewBox>
                <PreviewText className="mb-16">
                  {watch("recipient")}
                </PreviewText>
                <PreviewText>{watch("message")}</PreviewText>
                <PreviewText className="mt-16 text-center">
                  {watch("sender")}
                </PreviewText>
              </PreviewBox>
              <PreviewDesc>Inside</PreviewDesc>
            </div>
          </div>
        )}
        <div className="desktop:flex-row mb-24 flex flex-col gap-16">
          <div className="shrink grow basis-1/2">
            <div
              aria-labelledby="greeting-card-default-message"
              role="radiogroup"
            >
              <p className="text-18 leading-22 mb-18 tracking-tight">
                Select a default message
              </p>
              {selectedCardOption.default_messages.map((message, i) => (
                <Fragment key={`greeting-card-default-message-${i}`}>
                  {i > 0 && (
                    <Divider
                      aria-hidden
                      className="bg-grey-20 my-18 mx-auto"
                      style={{ width: "100px" }}
                    />
                  )}
                  <Radio
                    checked={watch("message") === message}
                    labelClassName="items-start"
                    name="default_message"
                    onChange={() => setValue("message", message)}
                    value={message}
                    variant="check"
                  >
                    <p className="text-14 leading-18">{message}</p>
                  </Radio>
                </Fragment>
              ))}
            </div>
          </div>
          <div className="flex shrink grow basis-1/2 flex-col">
            <p className="text-16 leading-22 desktop:mb-12 mb-4 tracking-tight">
              You can edit the default message or write your own.
            </p>
            <div className="mb-30 desktop:mb-12 desktop:order-none -order-1 flex gap-10">
              <div className="shrink grow basis-1/2">
                <Controller
                  control={control}
                  name="recipient"
                  render={({ field, fieldState: { error } }) => (
                    <Input
                      error={error}
                      id="recipient"
                      label="Recipient:"
                      placeholder="ie. To, Dear, Hey + Name"
                      {...field}
                    />
                  )}
                  rules={{ maxLength: "30" }}
                />
              </div>
              <div className="shrink grow basis-1/2">
                <Controller
                  control={control}
                  name="sender"
                  render={({ field, fieldState: { error } }) => (
                    <Input
                      error={error}
                      id="sender"
                      label="Sender:"
                      placeholder="ie. Love, From + Name"
                      {...field}
                    />
                  )}
                  rules={{ maxLength: "30" }}
                />
              </div>
            </div>
            <Controller
              control={control}
              name="message"
              render={({ field, fieldState: { error } }) => (
                <Textarea
                  className="border-grey-20 text-grey-65 focus:outline-hidden resize-none border px-10 py-12 focus:border-black"
                  data-cy="cart-line-item-greeting-card-message"
                  error={error}
                  id="message"
                  onDrop={(e) => handleDrop(e, setValue)}
                  onKeyDown={(e) => handleChange(e, setValue)}
                  onKeyUp={(e) => handleChange(e, setValue)}
                  onPaste={(e) => handleChange(e, setValue)}
                  rows="6"
                  {...field}
                />
              )}
              rules={{ maxLength: MAX_CHARS }}
            />
            <p
              className={cn(
                "text-12 tracking-tigher leading-12 mt-10 font-medium",
                overLimit ? "text-error" : "text-primary"
              )}
            >
              <span className={cn(overLimit ? "text-error" : "text-primary")}>
                {charsLeft}{" "}
                {overLimit ? "Characters Over Limit" : "Characters Left"}
              </span>{" "}
              &ndash; {MAX_CHARS} Characters Maximum
            </p>
          </div>
        </div>
        {errors?.root?.error && (
          <p className="text-error my-8">{errors.root.error.message}</p>
        )}
        <div className="desktop:flex-row flex flex-col justify-center gap-20">
          <Button
            className="tablet:w-300 tablet:order-none tablet:border tablet:border-primary text-primary tablet:no-underline order-1 w-full disabled:border-none"
            onClick={() => onClose()}
            underline
            variant="tertiary"
          >
            Cancel Message
          </Button>
          <Button
            className="tablet:w-300"
            disabled={overLimit}
            loading={isSubmitting}
            type="submit"
            variant="secondary"
          >
            Save Message
          </Button>
        </div>
      </form>
    </Modal>
  )
}

GreetingCardModal.propTypes = {
  greetingCardProps: PropTypes.object,
  handleGreetingCard: PropTypes.func,
  onClose: PropTypes.func,
  selectedCardOption: PropTypes.object,
  show: PropTypes.bool,
}
PreviewText.propTypes = {
  className: PropTypes.string,
}

export default GreetingCardModal
